/** Third party imports */
import { Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';

/** Local imports */
import { CreateUserStepsService } from '@core';
import { CreateTerritoryStepRoles } from '@features/users/interfaces';
import { TerritoryMapComponent } from '@shared/components';
import { getLocationFromCoordinates } from '@shared/utils';
import { NameTerritoryComponent } from './components';
import { TerritoryState } from './create-territory.interfaces';
import { getGeolocationPermissionStatus } from './create-territory.utils';

@Component({
    selector: 'app-create-territory',
    imports: [TerritoryMapComponent, NameTerritoryComponent],
    templateUrl: './create-territory.component.html',
    styleUrl: './create-territory.component.scss',
})
export class CreateTerritoryComponent {
    /**
     * Inject create user service to access selected user role and update draft
     */
    private createUserStepsService = inject(CreateUserStepsService);

    /**
     * Create territory map reference so we can access the validation fn
     */
    private createTerritoryMapRef = viewChild<TerritoryMapComponent>('territoryMapRef');

    /**
     * Name territory component reference so we can access the validation fn
     *
     * @public so it can be accessed from the `create-user.component.ts`
     */
    public nameTerritoryRef = viewChild<NameTerritoryComponent>('nameTerritoryRef');

    /**
     * Selected user role to be created
     */
    protected selectedUserType = this.createUserStepsService.selectedUserType;

    /**
     * Current sub step index
     */
    protected currentSubStepIndex = this.createUserStepsService.currentSubStepIndex;

    /**
     * Current step id. E.g create-territory. Since we need to make sure that we only access the sub-step for this specific step
     */
    protected currentStepId = this.createUserStepsService.currentStepId;

    /**
     * Coordinates of the territory
     */
    protected territory = signal<TerritoryState>({
        coordinates: [],
        color: '',
        undoedMarkers: [],
        undoPoints: 0,
        area: 0,
    });

    /**
     * @protected
     * @description Flag to show location permission prompt modal
     */
    protected readonly showLocationPrompt = signal<boolean>(false);

    /**
     * Track only the draft ID to prevent the hydration effect from re-running
     * every time a coordinate changes (which would break dragging).
     */
    private draftId = computed(() => {
        const role = this.selectedUserType()?.id;
        if (!role || (role as string) === 'none') return null;
        return (this.createUserStepsService.getDraft(role as any) as any)?.['id'];
    });

    /**
     * Flag whether user has not completed the drawing of the territory
     */
    protected isDrawingIncomplete = signal<boolean>(false);

    /**
     * Flag used to prevent the territory validation when onDelete is in operation still
     */
    public isDeleting = signal<boolean>(false);

    /**
     * Constructor to listen when the currentSubStepIndex is changed. This allows us hydrate the territory map with the data from the draft
     */
    constructor() {
        effect(() => {
            const id = this.draftId(); // The ONLY trigger for this effect
            const subStepIndex = this.currentSubStepIndex();
            const territoryMapRef = this.createTerritoryMapRef();
            const isMapReady = territoryMapRef?.isMapReady();

            if (!id || subStepIndex !== 0 || !territoryMapRef || !isMapReady) return;

            untracked(() => {
                const role = this.selectedUserType()?.id as CreateTerritoryStepRoles;
                const draftData = this.createUserStepsService.getDraft(role);
                const territoryData = draftData?.['create-territory'];

                if (territoryData && (territoryData.coordinates?.length ?? 0) > 0) {
                    // Hydrate territory data onto the map
                    territoryMapRef.hydrateTerritoryData(territoryData);
                } else {
                    // If no territory data, check for location permission
                    this.checkLocationPermission();
                }
            });
        });
    }

    /**
     * @private
     * @description Check for geolocation permission and show prompt if no territory is drawn
     * @returns Promise<void>
     */
    private async checkLocationPermission(): Promise<void> {
        const status = await getGeolocationPermissionStatus();
        if (status === 'prompt') {
            this.showLocationPrompt.set(true);
        } else if (status === 'granted') {
            this.createTerritoryMapRef()?.centerToUserLocation();
        }
    }

    /**
     * @protected
     * @description Handles the user's decision to allow location access from the custom prompt
     * @returns void
     */
    protected handleAllowLocation(): void {
        this.showLocationPrompt.set(false);
        this.createTerritoryMapRef()?.centerToUserLocation();
    }

    /**
     * Listen to when territory map emits the complete coordinates
     *
     * I just made it unknown since I don't have access to the leaflet instance here
     */
    protected onDone = (fired: {
        color: string;
        coordinates: number[][];
        undoedMarkers: { marker: unknown; latLang: unknown }[];
        undoPoints: number;
        area: number;
    }) => {
        this.territory.set(fired);

        // Silently save to in-memory draft (no navigation, no API call)
        // so visibilitychange / beforeunload can persist it
        this.createUserStepsService.patchDraftStep(
            this.selectedUserType()?.id as CreateTerritoryStepRoles,
            'create-territory',
            {
                color: fired.color,
                coordinates: fired.coordinates,
                undoedMarkers: fired.undoedMarkers,
                undoPoints: fired.undoPoints,
                area: fired.area,
            },
        );

        // Enable the next step from getting clicked using the stepper clicks
        this.createUserStepsService.setValidStep();
    };

    /**
     * When territory drawing is deleted, reset the territory
     */
    protected onDelete = () => {
        this.territory.set({
            coordinates: [],
            color: '',
            undoedMarkers: [],
            undoPoints: 0,
            area: 0,
        });

        // Clear coordinates on draft
        this.createUserStepsService.patchDraftStep(
            this.selectedUserType()?.id as CreateTerritoryStepRoles,
            'create-territory',
            {
                color: '',
                coordinates: [],
                undoedMarkers: [],
                undoPoints: 0,
                area: 0,
            },
        );

        // Disable the next step from getting clicked using the stepper clicks
        this.createUserStepsService.setInvalidStep(this.createUserStepsService.currentStepIndex());
    };

    /**
     * A public method available to be called on the parent component so clicking the next button performs territory validation
     * @public
     */
    public async validateTerritory(): Promise<boolean | { required: boolean }> {
        // Check if there are coordinates
        if (!this.territory().coordinates.length) {
            this.isDrawingIncomplete.set(true);
            return { required: true };
        } else {
            this.isDrawingIncomplete.set(false);
            return this.createTerritoryMapRef()?.checkTakenTerritories() ?? false;
        }
    }

    /**
     * Another validation to check if the create territory step is drawn or populated with markers
     */

    /**
     * A public method to validate the name territory sub-step.
     * Delegates to the NameTerritoryComponent's validation internally so the parent
     * doesn't need to reach through a nested viewChild chain.
     * @returns boolean True if the territory name is valid, false otherwise
     * @public So it can be called from the parent `create-user.component.ts`
     */
    public validateNameTerritory(): boolean {
        return this.nameTerritoryRef()?.validateTerritoryName() ?? false;
    }

    /**
     * A public method to save information to the draft db
     * @public So it can be accessed in the parent `create-user.component.ts`
     */
    public async saveInformation(): Promise<void> {
        // Set loading property to show loading indicator (This indicator is for doing external api calls, not the draftApi call)
        this.createUserStepsService.setIsLoading(true);

        // Get the state from the coordinates
        const address = await getLocationFromCoordinates(
            this.territory().coordinates[0][0],
            this.territory().coordinates[0][1],
        );

        // Hide the loading indicator
        this.createUserStepsService.setIsLoading(false);

        const userRole = this.createUserStepsService.selectedUserType()
            ?.id as CreateTerritoryStepRoles;
        const existingDraft = this.createUserStepsService.getDraft(userRole);
        const existingTerritoryData = existingDraft?.['create-territory'];

        this.createUserStepsService.updateStep(
            userRole,
            'create-territory',
            {
                territoryName: existingTerritoryData?.territoryName ?? '',
                color: this.territory().color,
                coordinates: this.territory().coordinates,
                assignedTo: existingTerritoryData?.assignedTo ?? '',
                state: address.state,
                createdAt: existingTerritoryData?.createdAt ?? new Date(),
                updatedAt: existingTerritoryData?.updatedAt ?? new Date(),
                undoedMarkers: this.territory().undoedMarkers,
                undoPoints: this.territory().undoPoints,
                area: this.territory().area,
            },
            false,
        ); // Not a main step increment
    }

    /**
     * A method to save the territoryName and move to the next step
     * @public So it can be called from the parent `create-user.component.ts`
     */
    public saveTerritoryName(): void {
        this.nameTerritoryRef()?.saveTerritoryName();
    }
}
